import { useEffect, useRef } from 'react'

import { IncidentQuery } from '@/graphql/generated/operations'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  closeTraceLogDrawer,
  selectIncidentTraceLogDrawerState,
} from '@/redux/ui/uiSlice'

import { shouldShowTraceLogs } from '../utils/feedUtils'

interface UseCloseTraceLogOnIneligibleIncidentIProps {
  incident: IncidentQuery['incident'] | undefined
}

/**
 * Closes the trace log drawer when the incident in view changes to one that
 * can't show trace logs (ENG-32091). The drawer's open state lives in
 * persisted redux, so without this it survives prev/next navigation from an
 * ACS incident onto e.g. a manual incident with no device, leaving the drawer
 * open on content that has no meaning for the incident on screen.
 */
export const useCloseTraceLogOnIneligibleIncident = ({
  incident,
}: UseCloseTraceLogOnIneligibleIncidentIProps): void => {
  const dispatch = useAppDispatch()
  const drawerState = useAppSelector(selectIncidentTraceLogDrawerState)
  const isOpen = drawerState?.isOpen
  // Set only by device deep links (Reasoning Trail); cleared on close.
  const linkedDeviceId = drawerState?.deviceId
  const isEligible = shouldShowTraceLogs(incident)
  const lastIncidentIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    // `incident` is undefined while a switch is in flight (the incident
    // drawer's stale-incident guard) — wait for the loaded incident.
    if (!incident?.id) {
      return
    }
    const isSameIncident = lastIncidentIdRef.current === incident.id
    lastIncidentIdRef.current = incident.id
    // A drawer deep-linked to a device fetches that device's logs itself, so
    // the incident's own devices changing under it (polling) doesn't make it
    // meaningless — only switching to another incident does.
    const isKeptByDeviceLink = isSameIncident && Boolean(linkedDeviceId)
    if (isOpen && !isEligible && !isKeptByDeviceLink) {
      dispatch(closeTraceLogDrawer())
    }
    // Keyed on the loaded incident's identity and eligibility — the drawer
    // polls every 3s, so devices can change without an id change. `isOpen` is
    // intentionally excluded: re-running when it flips would immediately close
    // a drawer just opened for the *current* incident — the Reasoning Trail
    // device deep-link opens it without passing the gate and must stay open.
  }, [incident?.id, isEligible])
}
